/* eslint-disable array-callback-return */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  BarChart,
  LineChart,
  TrendingUp,
  TrendingDown,
  Activity,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import Content from "../../../Components/Dashboard/Content/Content";
import Chart from "react-apexcharts";
import { useNavigate } from "react-router-dom";
import {
  getOpenOrder,
  GET_ALL_ORDERS,
  getQuantityUser,
} from "../../../Service/user.service";
import { usePrices } from "../../../context/PriceContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const prices = usePrices();
  
  const [activeTab, setActiveTab] = useState("BUY");
  const [sliderValue, setSliderValue] = useState(25);
  const [selectedSymbol, setSelectedSymbol] = useState("BTCUSD");
  const [openOrdersData, setOpenOrdersData] = useState([]);
  const [signalsData, setSignalsData] = useState([]);
  const [userQuantity, setUserQuantity] = useState([]);
  const [holdingQuantity, setHoldingQuantity] = useState(0);
  const [orderCount, setOrderCount] = useState({
    pendingOrders: 0,
    totalOrders: 0,
    openTrades: 0,
    closedTrades: 0,
  });

  const gotodashboard = JSON.parse(localStorage.getItem("user_details_goTo"));
  const isgotodashboard = JSON.parse(localStorage.getItem("gotodashboard"));
  const user_details = JSON.parse(localStorage.getItem("user_details"));

  const donutChartData = useMemo(
    () => {
      if (!userQuantity || userQuantity.length === 0) {
        return { labels: [], series: [] };
      }
      
      return {
        labels: userQuantity.map((item) => item.service_id?.name || 'Unknown'),
        series: userQuantity.map((item) => Number(item.quantity) || 0),
      };
    },
    [userQuantity]
  );

  function smartRound(price) {
    if (!price || isNaN(price)) return 0;
    const num = parseFloat(price);
    const intPart = Math.floor(Math.abs(num));
    if (intPart > 0) {
      return Math.round(num);
    } else {
      return parseFloat(num.toFixed(6));
    }
  }

  function smartFormat(price, DIGIT = 2) {
    if (price === null || price === undefined || price === "" || isNaN(price)) {
      price = 0;
    }

    let str = price.toString();
    if (!str.includes(".")) return str;

    let [intPart, decPart] = str.split(".");

    for (let i = 0; i < decPart.length; i += DIGIT) {
      let group = decPart.slice(i, i + DIGIT);
      if (parseInt(group) > 0) {
        let fixDecimals = i + group.length;
        return parseFloat(price).toFixed(fixDecimals);
      }
    }
    return parseFloat(price).toFixed(3);
  }

  const cryptoData = useMemo(() => {
    return userQuantity.map((item) => {
      const symbol = item.service_id.name;
      const priceData = prices[symbol] || {};
      
      const rawPrice = priceData.spot_price || priceData.price || 0;
      const formattedPrice = smartFormat(rawPrice);
      
      const rawChange = priceData.mark_change_24h || priceData.change || 0;
      const formattedChange = `${parseFloat(rawChange).toFixed(2)}%`;
      
      return {
        icon: (
          <img
            src={item.service_id.img}
            alt={item.service_id.name}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              marginRight: "5px",
            }}
          />
        ),
        name: `${item.service_id.name}`,
        token: symbol,
        date: "January",
        price: `$${formattedPrice}`,
        change: formattedChange,
        color: parseFloat(rawChange) >= 0 ? "text-success" : "text-danger",
      };
    });
  }, [userQuantity, prices]);

  const getClientOpenOrders = async () => {
    setOpenOrdersData({ loading: true, data: [] });

    const requestData = {
      _id: isgotodashboard ? gotodashboard.user_id : user_details.user_id,
    };

    try {
      const response = await getOpenOrder(requestData, user_details.token);

      if (response.status) {
        let OpenData = response.data;

        setOrderCount((prev) => ({
          ...prev,
          pendingOrders: OpenData.length,
        }));

        let firstThreeObjects = OpenData.slice(0, 3);
        setOpenOrdersData(firstThreeObjects);
      } else {
        setOpenOrdersData([]);
      }
    } catch (error) {
      console.error("Open Orders API Error:", error);
      setOpenOrdersData([]);
    }
  };

  const getClientsignals = async () => {
    try {
      const response = await GET_ALL_ORDERS({
        _id: isgotodashboard ? gotodashboard.user_id : user_details.user_id,
        token: isgotodashboard ? gotodashboard?.token : user_details.token,
      });

      if (response.status) {
        let OpenData = response.data;
        setOrderCount((prev) => ({
          ...prev,
          totalOrders: OpenData.length,
        }));

        let firstThreeObjects = OpenData.slice(0, 3);
        setSignalsData(firstThreeObjects);
      } else {
        setSignalsData([]);
      }
    } catch (error) {
      console.error("Orders API Error:", error);
      setSignalsData([]);
    }
  };

  const getQuantity = useCallback(async () => {
    try {
      const response = await getQuantityUser({
        id: isgotodashboard ? gotodashboard.user_id : user_details.user_id,
      });
      if (response.status) {
        setUserQuantity(response.data);
        setHoldingQuantity(response.quantity_count || 0);
        
        // Set first symbol as default if available
        if (response.data && response.data.length > 0) {
          setSelectedSymbol(response.data[0].service_id.name);
        }
      } else {
        console.error("Failed to fetch quantity data");
      }
    } catch (error) {
      console.error("Quantity API Error:", error);
    }
  }, [gotodashboard, isgotodashboard, user_details]);

  const chartOptions = useMemo(() => ({
    labels: [],
    colors: ["#6f42c1", "#212529", "#20c997", "#d63384", "#0d6efd", "#fd7e14"],
    legend: { 
      position: "bottom",
      fontSize: "12px"
    },
    chart: {
      type: 'donut',
      width: '100%',
      animations: {
        enabled: false
      },
      events: {}
    },
    states: {
      hover: {
        filter: {
          type: 'none'
        }
      },
      active: {
        filter: {
          type: 'none'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return val.toFixed(1) + "%"
      }
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total Holdings',
              fontSize: '14px',
              fontWeight: 600
            }
          }
        }
      }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'bottom'
        }
      }
    }]
  }), []);

  const DonutChart = useCallback(() => {
    if (!donutChartData.labels.length || 
        !donutChartData.series.length ||
        donutChartData.series.every(val => val === 0)) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Activity size={48} className="mb-3 opacity-50" />
          <p className="text-muted">No data available</p>
        </div>
      );
    }

    const validData = donutChartData.series.map((val, idx) => ({
      label: donutChartData.labels[idx],
      value: Number(val) || 0
    })).filter(item => item.value > 0);

    if (validData.length === 0) {
      return (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Activity size={48} className="mb-3 opacity-50" />
          <p className="text-muted">No holdings to display</p>
        </div>
      );
    }

    const finalOptions = {
      ...chartOptions,
      labels: validData.map(item => item.label)
    };

    return (
      <Chart
        options={finalOptions}
        series={validData.map(item => item.value)}
        type="donut"
        height={250}
      />
    );
  }, [donutChartData, chartOptions]);

  useEffect(() => {
    getClientOpenOrders();
    getClientsignals();
    getQuantity();
  }, []);

  return (
    <>
      <Content
        button_status={false}
        Page_title="Dashboard"
        Page_title_showClient="Live crypto portfolio & market updates."
      >
        <div className="dashboard-container">
          {/* Stats Cards Row */}
          <div className="row g-3 mb-4">
            <div className="col-lg-4 col-md-6 col-sm-6">
              <div className="enhanced-card p-4 h-100">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-info bg-opacity-10 rounded-3 p-3">
                      <BarChart className="text-info" size={24} />
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Assets</h6>
                    <h4 className="mb-0 fw-bold">{holdingQuantity || 0}</h4>
                    <small className="text-info">
                      <LineChart size={14} className="me-1" />
                      Holdings
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-6">
              <div
                className="enhanced-card p-4 h-100"
                onClick={() =>
                  navigate("/client/signals", {
                    state: { openBook: true },
                  })
                }
              >
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 rounded-3 p-3">
                      <TrendingUp className="text-success" size={24} />
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Pending Orders</h6>
                    <h4 className="mb-0 fw-bold">
                      {orderCount?.pendingOrders || 0}
                    </h4>
                    <small className="text-primary">
                      <Activity size={14} className="me-1" />
                      Live
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-6">
              <div
                className="enhanced-card p-4 h-100"
                onClick={() => navigate("/client/signals")}
              >
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                      <TrendingDown className="text-warning" size={24} />
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Total Orders</h6>
                    <h4 className="mb-0 fw-bold">
                      {orderCount?.totalOrders || 0}
                    </h4>
                    <small className="text-warning">
                      <ArrowDownRight size={14} className="me-1" />
                      Today
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Row */}
          <div className="row g-4 mb-4">
            {/* Hero Section */}
            <div className="col-xl-8 col-lg-7">
              <div className="enhanced-card p-1 h-100">
                <div className="gradient-border"></div>
                <div className="card-body p-2 position-relative">
                  <div className="row align-items-center">
                    <div className="col-lg-8 col-md-7">
                      <h2 className="fw-bold mb-3 text-gradient">
                        Trade 100+ Cryptocurrencies
                        <br />
                        <span className="text-primary">
                          Instantly & Securely
                        </span>
                      </h2>
                      <p className="text-muted mb-4 fs-6">
                        Experience zero-fee trading with lightning-fast
                        execution. Start your crypto journey with our advanced
                        trading platform.
                      </p>
                      <div className="d-flex gap-1 ">
                        <button
                          className="btn btn-primary btn-lg  trade-button"
                          onClick={() => navigate("/client/stocks")}
                        >
                          <TrendingUp className="me-2" size={20} />
                          Start Trading
                        </button>
                        <button
                          className="btn btn-outline-primary btn-lg  trade-button"
                          onClick={() => navigate("/client/signals")}
                        >
                          <Activity className="me-2" size={20} />
                          View Signals
                        </button>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-5 text-center">
                      <div className="position-relative d-inline-block">
                        <div
                          className="hero-circle rounded-circle d-flex align-items-center justify-content-center bg-gradient-primary"
                          style={{
                            width: "160px",
                            height: "160px",
                            background:
                              "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          }}
                        >
                          <span style={{ fontSize: "64px" }}>💎</span>
                        </div>
                        <div className="position-absolute top-0 end-0 bg-white rounded-circle p-2 shadow-sm">
                          <BarChart className="text-success" size={20} />
                        </div>
                        <div className="position-absolute bottom-0 start-0 bg-white rounded-circle p-2 shadow-sm">
                          <LineChart className="text-primary" size={20} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Trading Panel with Symbol Dropdown */}
            <div className="col-xl-4 col-lg-5">
              <div className="enhanced-card p-2 h-100">
                <div className="gradient-border"></div>
               
                <h5 className="fw-bold mb-4 text-center">Quick Trade</h5>

                <div className="btn-group w-100 mb-4" role="group">
                  <button
                    type="button"
                    className={`btn me-2 ${
                      activeTab === "BUY"
                        ? "btn-success"
                        : "btn-outline-success"
                    } fw-semibold`}
                    onClick={() => setActiveTab("BUY")}
                  >
                    <TrendingUp className="me-2" size={16} />
                    BUY
                  </button>

                  <button
                    type="button"
                    className={`btn ${
                      activeTab === "SELL" ? "btn-danger" : "btn-outline-danger"
                    } fw-semibold`}
                    onClick={() => setActiveTab("SELL")}
                  >
                    <TrendingDown className="me-2" size={16} />
                    SELL
                  </button>
                </div>

                {/* Symbol Dropdown */}
                <div className="mb-4">
                  <label className="form-label fw-semibold text-muted">
                    Select Symbol
                  </label>
                  <select
                    className="form-select"
                    value={selectedSymbol}
                    onChange={(e) => setSelectedSymbol(e.target.value)}
                    style={{ 
                      cursor: "pointer",
                      fontWeight: "600"
                    }}
                  >
                    {userQuantity.length > 0 ? (
                      userQuantity.map((item, index) => (
                        <option key={index} value={item.service_id.name}>
                          {item.service_id.name}
                        </option>
                      ))
                    ) : (
                      <option value="BTCUSD">BTCUSD</option>
                    )}
                  </select>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold text-muted">
                    Current Price
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <DollarSign size={16} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 price-ticker"
                      value={prices[selectedSymbol]?.spot_price || prices[selectedSymbol]?.price || "0.00"}
                      disabled
                      style={{ backgroundColor: "#f8f9fa" }}
                    />
                    <span className="input-group-text bg-primary text-white fw-bold">
                      {selectedSymbol.replace('USD', '')}
                    </span>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold text-muted">
                    Amount: {sliderValue}%
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min="0"
                    max="100"
                    value={sliderValue}
                    onChange={(e) => setSliderValue(e.target.value)}
                    style={{
                      background: `linear-gradient(to right, #667eea 0%, #764ba2 ${sliderValue}%, #e9ecef ${sliderValue}%, #e9ecef 100%)`,
                    }}
                  />
                  <div className="d-flex justify-content-between small text-muted mt-2">
                    <span>0%</span>
                    <span>25%</span>
                    <span>50%</span>
                    <span>75%</span>
                    <span>100%</span>
                  </div>
                </div>
<button
  className={`btn w-100 py-3 fw-bold fs-5 trade-button ${
    activeTab === "BUY" ? "btn-success" : "btn-danger"
  }`}
  disabled={isgotodashboard}
  onClick={() => {
    if (isgotodashboard) return;

    navigate("/client/stocks", {
      state: { searchTerm: selectedSymbol },
    });
  }}
>
  {activeTab === "BUY" ? (
    <>
      <TrendingUp className="me-2" size={20} />
      BUY {selectedSymbol.replace("USD", "")}
    </>
  ) : (
    <>
      <TrendingDown className="me-2" size={20} />
      SELL {selectedSymbol.replace("USD", "")}
    </>
  )}
</button>

              </div>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="row g-4">
            {/* Assets Allocation */}
            <div className="col-xl-4 col-lg-6">
              <div className="enhanced-card p-2 h-100">
                <div className="gradient-border"></div>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="fw-bold mb-0">Assets Allocation</h5>
                  <div className="connection-indicator connected"></div>
                </div>
                {DonutChart()}
              </div>
            </div>

            {/* Market Previews */}
            <div className="col-xl-4 col-lg-6">
              <div className="enhanced-card p-2 h-100">
                <div className="gradient-border"></div>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <h5 className="fw-bold mb-0">Market Previews</h5>
                  <div className="connection-indicator connected"></div>
                </div>
                <div className="market-stats">
                  {cryptoData.length > 0 ? (
                    cryptoData.map((crypto, index) => (
                      <div
                        key={index}
                        className="crypto-item d-flex justify-content-between align-items-center p-3 mb-3 rounded-3"
                        style={{ backgroundColor: "rgba(255,255,255,0.05)" }}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center me-3 fw-bold shadow-sm"
                            style={{
                              width: "50px",
                              height: "50px",
                              background:
                                crypto.color === "text-success"
                                  ? "linear-gradient(135deg, #dcfce7, #bbf7d0)"
                                  : "linear-gradient(135deg, #fee2e2, #fecaca)",
                              color:
                                crypto.color === "text-success"
                                  ? "#059669"
                                  : "#dc2626",
                            }}
                          >
                            {crypto.icon}
                          </div>
                          <div>
                            <div className="fw-bold text-dark">
                              {crypto.name}
                            </div>
                            <small className="text-muted">Live Price</small>
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="fw-bold fs-6 text-dark">
                            {crypto.price}
                          </div>
                          <small
                            className={`d-flex align-items-center justify-content-end ${crypto.color}`}
                          >
                            {crypto.color === "text-success" ? (
                              <ArrowUpRight size={12} className="me-1" />
                            ) : (
                              <ArrowDownRight size={12} className="me-1" />
                            )}
                            {crypto.change}
                          </small>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5 text-muted">
                      <Activity size={48} className="mb-3 opacity-50" />
                      <p>No market data available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Order Books */}
            <div className="col-xl-4 col-lg-12 pe-xl-0">
              <div className="d-flex flex-column gap-4 h-100">
                {/* Pending Orders */}
                <div
                  className="enhanced-card p-3 h-100 w-100"
                  style={{ position: "relative", borderRadius: "12px" }}
                >
                  <div className="gradient-border"></div>

                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5
                      className="fw-bold mb-0 text-primary cursor-pointer"
                      onClick={() =>
                        navigate("/client/signals", {
                          state: { openBook: true },
                        })
                      }
                    >
                      Pending Orders
                    </h5>
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: "#28a745",
                      }}
                    ></div>
                  </div>

                  <div className="table-responsive">
                    <div
                      className="small border-bottom pb-2 mb-2 fw-semibold text-muted"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 0.6fr 0.8fr",
                        alignItems: "center",
                        textAlign: "left",
                      }}
                    >
                      <span>Symbol</span>
                      <span>Type</span>
                      <span style={{ textAlign: "right" }}>Price</span>
                    </div>

                    {openOrdersData?.length ? (
                      openOrdersData.map((order, index) => (
                        <div
                          key={index}
                          className="border-bottom py-2"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 0.6fr 0.8fr",
                            alignItems: "center",
                            textAlign: "left",
                          }}
                        >
                          <span
                            className="fw-semibold text-truncate"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {order.symbol}
                          </span>
                          <span
                            className="badge text-center"
                            style={{
                              display: "inline-block",
                              minWidth: "55px",
                              fontSize: "0.75rem",
                              padding: "5px 0",
                              backgroundColor:
                                order.ordertype === "BUY"
                                  ? "#198754"
                                  : "#dc3545",
                            }}
                          >
                            {order.ordertype}
                          </span>
                          <span
                            className="text-success fw-bold"
                            style={{ textAlign: "right" }}
                          >
                            {order.price}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <Activity size={32} className="mb-2 opacity-50" />
                        <p className="mb-0">No open orders</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Orders */}
                <div
                  className="enhanced-card p-3 h-100 w-100"
                  style={{ position: "relative", borderRadius: "12px" }}
                >
                  <div className="gradient-border"></div>

                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5
                      className="fw-bold mb-0 text-info cursor-pointer"
                      onClick={() => navigate("/client/signals")}
                    >
                      Orders
                    </h5>
                    <div
                      style={{
                        width: "10px",
                        height: "10px",
                        borderRadius: "50%",
                        backgroundColor: "#28a745",
                      }}
                    ></div>
                  </div>

                  <div className="table-responsive">
                    <div
                      className="small border-bottom pb-2 mb-2 fw-semibold text-muted"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 0.6fr 0.8fr",
                        alignItems: "center",
                        textAlign: "left",
                      }}
                    >
                      <span>Symbol</span>
                      <span>Type</span>
                      <span style={{ textAlign: "right" }}>Price</span>
                    </div>

                    {signalsData?.length ? (
                      signalsData.map((order, index) => (
                        <div
                          key={index}
                          className="border-bottom py-2"
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 0.6fr 0.8fr",
                            alignItems: "center",
                            textAlign: "left",
                          }}
                        >
                          <span
                            className="fw-semibold text-truncate"
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {order.symbol}
                          </span>
                          <span
                            className="badge  text-center"
                            style={{
                              display: "inline-block",
                              minWidth: "55px",
                              fontSize: "0.75rem",
                              padding: "5px 0",
                              backgroundColor:
                                order.ordertype === "BUY"
                                  ? "#198754"
                                  : "#dc3545",
                            }}
                          >
                            {order.ordertype}
                          </span>
                          <span
                            className="text-success fw-bold"
                            style={{ textAlign: "right" }}
                          >
                            {smartRound(order.price)}
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4 text-muted">
                        <Activity size={32} className="mb-2 opacity-50" />
                        <p className="mb-0">No closed orders</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Content>
    </>
  );
};

export default Dashboard;